// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { shareImageFromUrl, type ShareImageOutcome } from '@/services/shareImage';
import { getTourPhotoUri } from '@/services/tourPhotoSource';

import type { TourPhoto } from './types';

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * The name the photo arrives under in WhatsApp, the recipient's gallery, or a desktop download —
 * so it reads as a golf photo rather than as a GUID. The id keeps two photos from the same hole
 * from overwriting each other in a download folder.
 */
export const tourPhotoFileName = (photo: TourPhoto): string => {
  const extension = EXTENSION_BY_CONTENT_TYPE[photo.contentType?.toLowerCase() ?? ''] ?? 'jpg';
  const suffix = photo.id.replace(/-/g, '').slice(0, 8);
  const where = photo.holeNumber !== null ? `hole-${photo.holeNumber}` : 'photo';
  return `goffy-${where}-${suffix}.${extension}`;
};

/**
 * Sends the photo itself to the device's share sheet.
 *
 * This is the counterpart to the public-link share, not a replacement for it: it works on a
 * private photo because the bytes are fetched with the member's own token and handed to the
 * chosen app directly. Nothing about the photo's visibility changes.
 */
export const shareTourPhoto = async ({
  photo,
  apiBaseUrl,
  token,
  title,
}: {
  photo: TourPhoto;
  apiBaseUrl: string;
  token: string | null | undefined;
  title: string;
}): Promise<ShareImageOutcome> => {
  const url = getTourPhotoUri(apiBaseUrl, photo.tourId, photo.id, 'main', photo.hasMain);
  if (!url) {
    return 'unsupported';
  }

  return shareImageFromUrl({
    url,
    token,
    fileName: tourPhotoFileName(photo),
    mimeType: photo.contentType || 'image/jpeg',
    title,
  });
};
